import { generateClient } from "aws-amplify/api";
import { sortBy } from "lodash";
import { ACADEMIC_PROGRAMS, DROPDOWNS, GRADUATE_CONTACT_SEARCH, FORMS_SEARCH, FORMS_SEARCH_FILTERS, FACULTY_SEARCH_FILTERS, CONTACT_SEARCH_FILTERS, SEARCH_PROGRAM_MAP, GRAD_RECORDS_SEARCH, TERMS, FACULTY_SEARCH, SEARCH_TERMS } from "../actions-index.js";
import * as Queries from '../graphql/queries';
import { ACTION_STATUS, ACTIVE, DEGREE } from "../util/constants";
import { dropdownSubtypes } from "../util/enums.js";
import { buildAction } from "../util/functions";
import { transformSearchForms, transformSearchGradRecs } from "./transform";

const API = generateClient();

export function getSearchAcademicTerms(){
    return async (dispatch) => {
        try {
            dispatch(buildAction(SEARCH_TERMS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getSearchAcademicTerms });
            const academicTerms = (response?.data?.getSearchAcademicTerms?.terms ?? []);
            if(!response || !academicTerms || !academicTerms?.length) {
                return dispatch(buildAction(SEARCH_TERMS, {
                    status: ACTION_STATUS.SUCCESS,
                    data: [],
                }));
            }
            const sortedAcademicTerms = sortBy(academicTerms, ["termCode"]);
            dispatch(buildAction(SEARCH_TERMS, {
                status: ACTION_STATUS.SUCCESS,
                data: sortedAcademicTerms
            }));                       
            const optionsAcademicTerms = sortedAcademicTerms.map((term) => ({
                id: term.termCode,
                title: term.termDescription,
                value: term.termCode
            }));
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.searchTerms, 
                data: optionsAcademicTerms
            }));
        } catch (error) {
            console.error(error);
            dispatch(buildAction(SEARCH_TERMS, { status: ACTION_STATUS.ERROR }));
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.searchTerms, 
                status: ACTION_STATUS.ERROR
            }));
        }
    }
}

export function getAcademicPrograms() {
    return async (dispatch) => {
        try {
            let commonComponentArray = []
            dispatch(buildAction(ACADEMIC_PROGRAMS, { status: ACTION_STATUS.LOADING }));
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.graduatePrograms, 
                status: ACTION_STATUS.LOADING
            }));
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.proposedGraduatePrograms, 
                status: ACTION_STATUS.LOADING
            }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { status: "A" } } });
            const academicPlans = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            if(!response || !academicPlans || !academicPlans?.length) {
                return dispatch(buildAction(ACADEMIC_PROGRAMS, { 
                    status: ACTION_STATUS.SUCCESS, 
                    data: [],
                }));
            }
            let academicPrograms = new Set();

            let index = 0
            for(const academicPlan of academicPlans) {
                if(academicPlan.graduateProgram) {
                    academicPrograms.add(academicPlan.graduateProgram);
                    let newPlan = {
                        id: academicPlan.academicPlan + academicPlan.graduateProgram + index,
                        title: academicPlan.academicPlan,
                        value: academicPlan.academicPlan,
                        gradLevel: academicPlan?.gradLevel,
                        degree: academicPlan?.degree,
                        academicPlan: academicPlan.graduateProgram, // used to filter faculty dropdowns by selected grad plan (faculty use program) 
                        program: academicPlan.graduateProgram, // used to filter faculty dropdowns by the user's program
                        proposedAcadPlan: academicPlan.graduateProgram, // used to filter faculty dropdowns by selected proposed grad plan (faculty use program) 
                    }
                    if(academicPlan.acadProgDescr) {
                        newPlan.title += ` - ${academicPlan.descr}`
                    }
                    commonComponentArray.push(newPlan)
                }
                index++    
            }
            academicPrograms = Array.from(academicPrograms);
            academicPrograms.sort();
            dispatch(buildAction(ACADEMIC_PROGRAMS, { 
                status: ACTION_STATUS.SUCCESS, 
                data: academicPrograms
            }));

            const sortedCommonComponentArray = sortBy(commonComponentArray, ["title"]);
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.graduatePrograms, 
                data: sortedCommonComponentArray
            }));

            const filteredCommonComponentArray = sortedCommonComponentArray.filter((academicPlan) => {
                return (![DEGREE.CERTIFICATE, DEGREE.INTERDISCIPLINARY_SPECIALIZATION, DEGREE.MINOR, DEGREE.SPECIALIZATION].includes(academicPlan.degree));
            });
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.proposedGraduatePrograms, 
                data: filteredCommonComponentArray
            }));
        } catch(error) {
            console.error("Get Academic Programs Error: ", error);
            dispatch(buildAction(ACADEMIC_PROGRAMS,{ status: ACTION_STATUS.ERROR }));
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.graduatePrograms, 
                status: ACTION_STATUS.ERROR
            }));
            dispatch(buildAction(DROPDOWNS, { 
                type: dropdownSubtypes.proposedGraduatePrograms, 
                status: ACTION_STATUS.ERROR
            }));
        }
    };
}

export function getSearchProgramMap() {
    return async (dispatch, getState) => {
        let currentUser = getState().authentication?.user
        const shouldFilterPlans = !(currentUser?.isAdmin || currentUser?.isInquirer || currentUser?.isStudent)
        const programAccessSet = new Set()
        if (shouldFilterPlans) {
            let appliactionRoles = currentUser?.applicationRoles || []
            appliactionRoles.forEach( (role) => programAccessSet.add((role.match(/#([^-]+)-?/) || [])[1] || '') )
        }
        try {
            dispatch(buildAction(SEARCH_PROGRAM_MAP, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getAcademicPlans, variables: { filter: { status: "A" } } });
            const academicPlans = (response?.data?.getAcademicPlans?.academicPlans ?? []);
            const graduateProgramMap = {};
            for(const academicPlan of academicPlans) {
                if(shouldFilterPlans && !programAccessSet.has(academicPlan.graduateProgram)) continue
                if(academicPlan.degree !== DEGREE.SPECIALIZATION) {
                    if(!graduateProgramMap[academicPlan.graduateProgram]) {
                        graduateProgramMap[academicPlan.graduateProgram] = { acadProgDescr: "", academicPlans: [] };
                    }
                    if(academicPlan.acadProgDescr) graduateProgramMap[academicPlan.graduateProgram].acadProgDescr = academicPlan.acadProgDescr;
                    graduateProgramMap[academicPlan.graduateProgram].academicPlans.push({ academicPlan: academicPlan.academicPlan, descr: academicPlan.descr, status: academicPlan.status });
                }
            }
            dispatch(buildAction(SEARCH_PROGRAM_MAP, { status: ACTION_STATUS.SUCCESS, data: graduateProgramMap, filtered: shouldFilterPlans }));
        } catch(error) {
            console.error("Get Graduate Program Map Error: ", error);
            dispatch(buildAction(SEARCH_PROGRAM_MAP,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function getGradRecTerms(options) {
    const { career = "GRAD", showFromPresent, showHistorical} = options
    let variables = { career }
    if(showFromPresent) {
        variables.future = 'Y'
    }
    if(showHistorical) {
        variables.history = 'Y'
    }

    return async (dispatch) => {
        try {
            dispatch(buildAction(TERMS, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getTerms, variables });
            const terms = response?.data?.getTerms?.terms
            if(!terms.length) {
              throw new Error('unable to get terms')
            }

            dispatch(buildAction(TERMS, { status: ACTION_STATUS.SUCCESS, data: terms }));
        } catch(error) {
            console.error("Get Terms Error: ", error);
            dispatch(buildAction(TERMS,{ status: ACTION_STATUS.ERROR }));
        }
    };
}


export function searchGraduateContacts(academicPlan, affiliation, query) {
    return async (dispatch) => {
        try {
            dispatch(buildAction(GRADUATE_CONTACT_SEARCH, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.getGraduateContacts, variables: { filter: { academicPlan, active: ACTIVE.YES, affiliation, query } } });
            const graduateContacts = (response?.data?.getGraduateContacts?.graduateContacts ?? []);
            dispatch(buildAction(GRADUATE_CONTACT_SEARCH, { status: ACTION_STATUS.SUCCESS, data: graduateContacts }));
        } catch(error) {
            console.error("Search Graduate Contacts Error: ", error);
            dispatch(buildAction(GRADUATE_CONTACT_SEARCH,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function resetGraduateContactSearch() {
    return (dispatch) => {
        dispatch(buildAction(GRADUATE_CONTACT_SEARCH, { status: "" }));
    };
}

export function searchFaculty(filter = {active: null, program: null, query: ""}) {
    return async (dispatch) => {
        const { active, program, query } = filter;
        try {
            dispatch(buildAction(FACULTY_SEARCH, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.searchGraduateFaculty, variables: { active, program, query } });
            const graduateFaculty = (response?.data?.searchGraduateFaculty?.graduateFaculty ?? []);
            dispatch(buildAction(FACULTY_SEARCH, { status: ACTION_STATUS.SUCCESS, data: graduateFaculty }));
        } catch(error) {
            console.error("Search Graduate Faculty Error: ", error);
            dispatch(buildAction(FACULTY_SEARCH,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function searchForms(filter) {
    return async (dispatch) => {
        try {
            dispatch(buildAction(FORMS_SEARCH, { status: ACTION_STATUS.LOADING }));
            const response = await API.graphql({ query: Queries.searchForms, variables: { filter: filter } });
            const forms = (response?.data?.searchForms?.forms ?? []);
            dispatch(buildAction(FORMS_SEARCH, { status: ACTION_STATUS.SUCCESS, data: transformSearchForms(forms) }));
        } catch(error) {
            console.error("Search Forms Error: ", error);
            dispatch(buildAction(FORMS_SEARCH,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function setFormsSearchFilters(filters) {
    return (dispatch) => {
        dispatch(buildAction(FORMS_SEARCH_FILTERS, { data: filters }));
    };
}

export function resetFormsSearch() {
    return (dispatch) => {
        dispatch(buildAction(FORMS_SEARCH, { status: "" }));
    };
}

export function clearFormsSearchFilters() {
    return (dispatch) => {
        dispatch(buildAction(FORMS_SEARCH_FILTERS, { data: null }));
    };
}

export function setFacultySearchFilters(filters) {
    return (dispatch) => {
        dispatch(buildAction(FACULTY_SEARCH_FILTERS, { data: filters }));
    };
}

export function clearFacultySearchFilters() {
    return (dispatch) => {
        dispatch(buildAction(FACULTY_SEARCH_FILTERS, { data: null }));
    };
}

export function setContactSearchFilters(filters) {
    return (dispatch) => {
        dispatch(buildAction(CONTACT_SEARCH_FILTERS, { data: filters }));
    };
}

export function clearContactSearchFilters() {
    return (dispatch) => {
        dispatch(buildAction(CONTACT_SEARCH_FILTERS, { data: null }));
    };
}

export function searchGraduationRecords(emplid, partialSk, filter) {
    return async (dispatch) => {
        try {
            dispatch(buildAction(GRAD_RECORDS_SEARCH, { status: ACTION_STATUS.LOADING }));
            let options = {}; // optioins to marshall pk and partialSk or not
            const response = await API.graphql({ query: Queries.getGraduationRecords, variables: { emplid, partialSk, filter } });
            const graduationRecords = (response?.data?.getGraduationRecords?.graduationRecords ?? []);
            dispatch(buildAction(GRAD_RECORDS_SEARCH, { status: ACTION_STATUS.SUCCESS, data: transformSearchGradRecs(graduationRecords, filter) }));
        } catch(error) {
            console.error("Search Forms Error: ", error);
            dispatch(buildAction(GRAD_RECORDS_SEARCH,{ status: ACTION_STATUS.ERROR }));
        }
    };
}

export function resetGraduationRecordsSearch() {
    return (dispatch) => {
        dispatch(buildAction(GRAD_RECORDS_SEARCH, { status: "" }));
    };
}